import {Banner, Stack} from '@primer/react'
import {Suspense, use, useState} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {CommentsSkeleton} from './CommentsSkeleton'
import {CommentsErrorFallback} from './CommentsErrorFallback'
import {LazyThreadsDataLoader, useShouldLoadLazyThreadData} from './LazyThreadsDataLoader'
import {ThreadPreviewsContent} from './ThreadPreviewsContent'
import {IssueCommentsContent} from './IssueCommentsContent'
import {CommentsContext, ViewMode as ViewModeValues} from './CommentsContext'
import type {ConversationItem} from './utils/build-conversation-items'
import {
  isIssueCommentComposerEnabled,
  IssueCommentComposer,
  type IssueCommentComposerHandle,
} from './IssueCommentComposer'
import {useLayoutCanComment} from '../../stores/layout-selectors'
import {CommentsSort, type CommentsFilterState} from './use-comments-filter-state'
import {isReviewCommentsMode} from './use-filtered-comments-data'
import {Blankslate} from '@primer/react/experimental'
import {CodeReviewIcon, CommentDiscussionIcon} from '@primer/octicons-react'

export interface CommentsProps {
  handleQuoteReply: (quotedText: string) => void
  filteredConversationItems: ConversationItem[]
  /** The maximum number of latest comments shown when the limit is exceeded. */
  limitCount?: number
  /** Whether the number of comments exceeds the displayable limit. */
  limitExceeded?: boolean
  /**
   * Called after the user activates a thread preview's link to its comment in the diff.
   * Navigating to the comment is handled internally, so this is only for side effects the
   * consumer wants to layer on top (e.g. closing a panel that renders these comments).
   */
  onNavigateToDiffComment?: () => void
  /** The current file path name used to scope displayed comments. */
  pathName: string
  /** The login handle of the pull request author, used for issue comments rendering. */
  pullRequestAuthorLogin: string
  totalConversationItemsCount: number
  hideCommentComposer?: boolean
  filterState: CommentsFilterState
  composerRef?: React.Ref<IssueCommentComposerHandle>
}

/**
 * Renders pull request comments.
 *
 * Displays either thread previews or issue comments based on the current `viewMode`.
 * When in `thread-previews` mode and the comment limit is exceeded, a warning banner
 * is shown indicating that only the latest comments are visible.
 *
 * Content is lazily loaded with a skeleton fallback.
 */
export function Comments({
  handleQuoteReply,
  filteredConversationItems,
  limitCount,
  limitExceeded,
  onNavigateToDiffComment,
  pathName,
  pullRequestAuthorLogin,
  totalConversationItemsCount,
  hideCommentComposer,
  filterState,
  composerRef,
}: CommentsProps) {
  const {viewMode} = use(CommentsContext)
  const {shouldLazyLoadData, isLazyLoading, onLazyDataLoaded} = useShouldLoadLazyThreadData()

  const reviewCommentsMode = isReviewCommentsMode(filterState)
  const filteredThreads = reviewCommentsMode
    ? filteredConversationItems.filter(item => item.type === 'thread').map(item => item.data)
    : []

  const [commentComposerCollapsed, setCommentComposerCollapsed] = useState(true)
  const showCommentComposer =
    isIssueCommentComposerEnabled(reviewCommentsMode, useLayoutCanComment()) && !hideCommentComposer
  const commentComposer = showCommentComposer && (
    <IssueCommentComposer
      ref={composerRef}
      // ensure the composer stays mounted across view modes to preserve draft (at least until we move draft into local storage)
      collapsed={commentComposerCollapsed}
      onCollapsedChange={setCommentComposerCollapsed}
    />
  )
  const commentComposerAtTop = filterState.sort === CommentsSort.NewestFirst

  return (
    <Stack direction="vertical" gap="normal">
      {shouldLazyLoadData && <LazyThreadsDataLoader onLoaded={onLazyDataLoaded} />}

      {commentComposerAtTop && commentComposer}
      {totalConversationItemsCount > 0 && totalConversationItemsCount !== filteredConversationItems.length && (
        <p className="fgColor-muted text-small text-center m-0">
          Showing {filteredConversationItems.length} of {totalConversationItemsCount}{' '}
          {reviewCommentsMode && <>review </>}
          <> comments</>
        </p>
      )}
      {reviewCommentsMode && limitExceeded && (
        <Banner
          aria-label="Warning"
          title="Warning"
          variant="warning"
          hideTitle
          description={`Only the ${limitCount} latest comments are being shown.`}
        />
      )}
      <ErrorBoundary boundaryName="Comments" fallback={<CommentsErrorFallback />}>
        <Suspense fallback={<CommentsSkeleton />}>
          {viewMode === ViewModeValues.ThreadPreviews && isLazyLoading ? (
            <CommentsSkeleton /> // TODO: just use suspense
          ) : reviewCommentsMode ? (
            // TODO: we should be able to render thread previews using IssueComments instead, allowing us to unify
            // on one code path. However, currently the issue comments component triggers that data to load via
            // `useLoadAllIssueCommentsAndReviews`.
            // Ideally we should be able to move the data loading into the filtered data hook and move that into the
            // issue comments component
            <ThreadPreviewsContent onNavigateToDiffComment={onNavigateToDiffComment} threads={filteredThreads} />
          ) : (
            <IssueCommentsContent
              filteredConversationItems={filteredConversationItems}
              pathName={pathName}
              pullRequestAuthorLogin={pullRequestAuthorLogin}
              onNavigateToDiffComment={onNavigateToDiffComment}
              onQuoteReply={handleQuoteReply}
            />
          )}
          {totalConversationItemsCount === 0 && !isLazyLoading && (
            // Separate from the content because the content must render to load the data
            <div className="d-flex flex-column position-relative width-full height-full flex-justify-center">
              {reviewCommentsMode ? (
                <Blankslate>
                  <Blankslate.Visual>
                    <CodeReviewIcon size="medium" />
                  </Blankslate.Visual>
                  <Blankslate.Heading>No review comments yet</Blankslate.Heading>
                </Blankslate>
              ) : (
                <Blankslate>
                  <Blankslate.Visual>
                    <CommentDiscussionIcon size="medium" />
                  </Blankslate.Visual>
                  <Blankslate.Heading>No comments yet</Blankslate.Heading>
                  <Blankslate.Description>Add a comment to start the conversation.</Blankslate.Description>
                </Blankslate>
              )}
            </div>
          )}
        </Suspense>
      </ErrorBoundary>
      {!commentComposerAtTop && commentComposer}
    </Stack>
  )
}
